/**
 * Opening a file on Arrange or the Lab board, whatever it is.
 *
 * MusicXML and MIDI are read in the browser at once (`arrange-pick.ts`). A PDF of printed
 * music cannot be, nor photos of its pages, picked together and made into one PDF on the
 * server first (`pictures.ts`): it is uploaded, the server reads it with its own reader, or
 * Audiveris where it has none or that fails (docs/06, "Reading a scan"), and the score that comes back is picked as any other, with a note saying a machine read it and it
 * has to be checked against the page. That takes tens of seconds a page, so it is a job that
 * is followed and can be stopped, drawn by the strip a run is drawn by.
 *
 * A scan of several pages is opened page by page: each time the pages read from the first grow,
 * they are picked as the score so far, to be played and looked at while the rest are read, and
 * the whole replaces them once it is in (docs/06, "Reading a scan"). `ready` says how far it
 * has got. Stopped, or failed after its first pages, the pages read stay open, their note
 * saying how far the reading got; before its first pages, what was open stays open. Leaving
 * the board stops a reading still going: nothing would ever collect it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { RunProgress } from "./arrange-job";
import { type Picked, pickScore } from "./arrange-pick";
import { said } from "./job-poll";
import { PAGES_ACCEPT, asPdf, called, isLonePdf, isPage } from "./pictures";
import { scanRead } from "./scan-read";

/**
 * What a file picker offers: MusicXML plain or zipped, MIDI, and a PDF or photos of printed
 * music.
 */
export const SCORE_ACCEPT = `.musicxml,.xml,.mxl,.mid,.midi,application/vnd.recordare.musicxml+xml,application/vnd.recordare.musicxml,text/xml,audio/midi,${PAGES_ACCEPT}`;

/** How far a scan opened page by page has got: its pages, and which are still being read. */
export interface ScanReady {
  readonly pages: number;
  /** The pages not read yet, counted from 1. */
  readonly waiting: readonly number[];
}

export interface OpenScore {
  /** What a scan being read last reported, or `null` when none is. */
  readonly progress: RunProgress | null;
  /** Photos of pages being read, what they are called and how many, or `null`. */
  readonly photos: { readonly name: string; readonly count: number } | null;
  /** How far the scan being read has got, once its first pages are open; else `null`. */
  readonly ready: ScanReady | null;
  /** A score picked: one file, or several photos or pictures of its pages. */
  open(files: readonly File[]): void;
  /** Stop the scan being read, keeping the pages read so far. */
  cancel(): void;
}

/** `onPicked` is told each score opened, and `null` for a file that is not one. */
export function useOpenScore(onPicked: (picked: Picked | null) => void): OpenScore {
  const latest = useRef(onPicked);
  latest.current = onPicked;
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [photos, setPhotos] = useState<OpenScore["photos"]>(null);
  const [ready, setReady] = useState<ScanReady | null>(null);
  // Each file opened gets a number, checked before anything of it touches the page: a scan
  // that was stopped, or replaced by another file, must not land late.
  const generation = useRef(0);
  const job = useRef<string | null>(null);
  // The pages read so far, open while the rest are read: what stays open if the rest never are.
  const sofar = useRef<Picked | null>(null);

  /** The pages read so far left open, saying the reading `ended` before the rest. */
  const keep = useCallback((ended: string): void => {
    const kept = sofar.current;
    sofar.current = null;
    if (kept === null) return;
    const note = `${kept.note ?? ""} The reading ${ended} there: the pages after them are not in this score.`;
    latest.current({ ...kept, note: note.trim() });
  }, []);

  const stop = useCallback((): void => {
    generation.current += 1;
    const running = job.current;
    job.current = null;
    if (running !== null) void api.cancelRecognise(running).catch(() => undefined);
  }, []);

  useEffect(() => stop, [stop]);

  const open = useCallback(
    (files: readonly File[]): void => {
      stop();
      const name = called(files);
      const mine = generation.current;
      const live = (): boolean => generation.current === mine;
      setProgress(null);
      setPhotos(null);
      setReady(null);
      sofar.current = null;

      /** The pages read into a score; `null` when the reading was stopped. */
      const scanned = async (): Promise<Picked | null> => {
        // A PDF alone is the scan; anything else is photos, made into one first.
        if (!(await isLonePdf(files))) setPhotos({ name, count: files.length });
        const pdf = await asPdf(files, live, setProgress);
        if (pdf === null) return null;
        const read = await scanRead(
          pdf,
          undefined,
          live,
          setProgress,
          (started) => {
            job.current = started;
          },
          (partial) => {
            if (!live()) return;
            setReady({ pages: partial.pages, waiting: partial.waiting });
            sofar.current = { name, text: partial.musicxml, note: partial.note };
            latest.current(sofar.current);
          },
        );
        return read && { name, text: read.musicxml, note: read.note };
      };

      const picking = async (): Promise<void> => {
        // Only their first bytes: a scan is sent as the file it is, and one too large to send
        // is refused before any of it is held here.
        const pages = await Promise.all(files.map(isPage));
        if (!pages.every(Boolean)) {
          const [file] = files;
          if (file === undefined) return;
          const odd = files[pages.indexOf(false)];
          if (files.length > 1 && odd !== undefined) {
            throw new Error(
              `${odd.name} is not a PDF or a photo of a page; several files are read as one score only when every one is`,
            );
          }
          const got = await pickScore(file.name, new Uint8Array(await file.arrayBuffer()));
          if (!live()) return;
          latest.current(typeof got === "string" ? null : got);
          if (typeof got === "string") window.alert(got);
          return;
        }
        const got = await scanned();
        if (!live()) return;
        sofar.current = null;
        job.current = null;
        setProgress(null);
        setPhotos(null);
        setReady(null);
        if (got !== null) latest.current(got);
      };

      picking().catch((cause: unknown) => {
        if (!live()) return;
        job.current = null;
        setProgress(null);
        setPhotos(null);
        setReady(null);
        keep("failed");
        window.alert(`${name} could not be read: ${said(cause)}`);
      });
    },
    [stop],
  );

  // The pages read so far are already picked: stopping leaves them open, saying so.
  const cancel = useCallback((): void => {
    stop();
    setProgress(null);
    setPhotos(null);
    setReady(null);
    keep("was stopped");
  }, [stop, keep]);

  return { progress, photos, ready, open, cancel };
}
